import InAppSpy from 'inapp-spy'

import { CLIENT_QUERY } from '@/router/query-params'

import { compareVersionStrings } from './device'
import safeURLParse from './safeURLParse'

// Branded iOS browsers can expose bridges and lack Safari's WebPageNamespace.
const THIRD_PARTY_BROWSER_USER_AGENT_PATTERN = /\b(?:CriOS|FxiOS|EdgiOS|OPiOS|OPT|YaBrowser|DuckDuckGo)\b/

export type BrowserHandoff = {
    method: 'location' | 'meta-refresh' | 'intent'
    parentApp: string
    url: string
}

interface InAppBrowserEnvironment {
    self: unknown
    top: unknown
    navigator: Pick<Navigator, 'maxTouchPoints' | 'userAgent'> & {
        standalone?: boolean
        userAgentData?: { platform?: string; brands?: ReadonlyArray<{ brand: string }> }
    }
    PlaygamaApp?: unknown
    browser?: unknown
    matchMedia?: (query: string) => Pick<MediaQueryList, 'matches'>
    webkit?: {
        messageHandlers?: {
            playgamaApp?: unknown
        }
    }
}

interface HandoffEnvironment {
    location: Pick<Location, 'assign' | 'replace'>
    document: Pick<Document, 'createElement' | 'head' | 'body'>
}

export function detectInAppBrowser(
    environment: InAppBrowserEnvironment,
    targetUrl: URL,
    force = false,
): BrowserHandoff | undefined {
    if (environment.self !== environment.top || targetUrl.searchParams.get(CLIENT_QUERY.ESCAPED_FROM_WEBVIEW)) {
        return undefined
    }
    if (targetUrl.protocol !== 'http:' && targetUrl.protocol !== 'https:') {
        return undefined
    }

    const { maxTouchPoints, userAgent, userAgentData } = environment.navigator
    const isStandalone =
        environment.navigator.standalone || environment.matchMedia?.('(display-mode: standalone)').matches
    const isPlaygamaApp = environment.PlaygamaApp || environment.webkit?.messageHandlers?.playgamaApp
    if (isStandalone || isPlaygamaApp) {
        return undefined
    }

    const url = safeURLParse(targetUrl.href)
    if (!url) {
        return undefined
    }
    url.searchParams.delete(CLIENT_QUERY.FORCE_HANDOFF)
    const detectedApp = InAppSpy({ ua: userAgent })

    const isIos = /(iPhone|iPad|iPod)/.test(userAgent) || (/Macintosh/.test(userAgent) && maxTouchPoints > 1)
    if (isIos) {
        const safariVersion = /\bVersion\/(\d+(?:\.\d+)*)/.exec(userAgent)?.[1]
        if (safariVersion && /\bSafari\//.test(userAgent) && !compareVersionStrings(safariVersion, '15.4')) {
            return undefined
        }
        if (!force && (THIRD_PARTY_BROWSER_USER_AGENT_PATTERN.test(userAgent) || detectedApp.appKey === 'twitter')) {
            return undefined
        }

        const parentApp =
            detectedApp.appKey === 'instagram' || detectedApp.appKey === 'threads'
                ? `ios-${detectedApp.appKey}`
                : 'ios-generic'
        url.searchParams.set(CLIENT_QUERY.ESCAPED_FROM_WEBVIEW, parentApp)

        if (detectedApp.appKey === 'instagram') {
            return {
                method: 'meta-refresh',
                parentApp,
                url: `instagram://extbrowser/?url=${encodeURIComponent(url.href)}`,
            }
        }

        // Threads uses its original Barcelona name in the iOS UA and external-browser scheme.
        // https://github.com/m11to/inapp-escape/blob/master/src/index.js
        if (detectedApp.appKey === 'threads') {
            return {
                method: 'meta-refresh',
                parentApp,
                url: `barcelona://extbrowser/?url=${encodeURIComponent(url.href)}`,
            }
        }

        // Safari namespace signal used by FingerprintJS's browser research:
        // https://github.com/fingerprintjs/fingerprintjs/blob/master/src/utils/browser.ts
        if (!force && String(environment.browser) === '[object WebPageNamespace]') {
            return undefined
        }

        return {
            method: 'location',
            parentApp,
            url: url.href.replace(/^https?:/, 'x-safari-https:'),
        }
    }

    const isAndroid = /\bAndroid\b/i.test(userAgent) || userAgentData?.platform === 'Android'
    if (isAndroid) {
        // Keep generic WebView signals alongside the library's app-specific UA and bridge detection.
        const isWebView =
            /\bwv\b/.test(userAgent) ||
            userAgentData?.brands?.some(({ brand }) => brand === 'Android WebView') ||
            detectedApp.isInApp
        if (!isWebView) {
            return undefined
        }

        const parentApp = `android-${detectedApp.appKey || 'generic'}`
        url.searchParams.set(CLIENT_QUERY.ESCAPED_FROM_WEBVIEW, parentApp)

        return {
            method: 'intent',
            parentApp,
            url: `${url.href.replace(/^https?:/, 'intent:')}#Intent;scheme=${url.protocol.slice(0, -1)};action=android.intent.action.VIEW;category=android.intent.category.BROWSABLE;end`,
        }
    }

    return undefined
}

export function requestBrowserHandoff(handoff: BrowserHandoff, environment: HandoffEnvironment = window): void {
    if (handoff.method === 'intent') {
        requestIntentHandoff(handoff.url, environment.document)
        return
    }

    if (handoff.method === 'meta-refresh') {
        const meta = environment.document.createElement('meta')
        meta.httpEquiv = 'refresh'
        meta.content = `0;url=${handoff.url}`
        environment.document.head.appendChild(meta)
        return
    }

    environment.location.replace(handoff.url)
}

export function openBrowserHandoff(handoff: BrowserHandoff, environment: HandoffEnvironment = window): void {
    if (handoff.method === 'intent') {
        requestIntentHandoff(handoff.url, environment.document)
        return
    }

    environment.location.assign(handoff.url)
}

function requestIntentHandoff(url: string, document: HandoffEnvironment['document']): void {
    // An unhandled scheme must fail in a child frame so the portal and fallback timer survive.
    // Opening an external app still depends on the host accepting subframe navigation.
    const frame = document.createElement('iframe')
    frame.hidden = true
    frame.src = url
    document.body.appendChild(frame)
    // Cleanup only: this delay does not determine whether the handoff succeeded.
    setTimeout(() => frame.remove(), 10_000)
}
